import axios from 'axios';
import * as Constants from '../Constant/constant';
import * as authSession from '../Constant/authSession';
import { createSingleFlight } from './refreshQueue';

const api = axios.create({
  baseURL: Constants.API_PATH,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// A separate, plain instance for the refresh call itself — deliberately
// NOT the `api` instance above, so a failing refresh can never re-enter
// this same response interceptor and recurse.
const refreshClient = axios.create({
  baseURL: Constants.API_PATH,
  withCredentials: true
});

// Every authenticated backend route reads staff identity/role/library scope
// from the verified JWT (see lms-backend/middleware/authMiddleware.js) — it
// never trusts request body fields like received_by_staff_id or library_id.
// This attaches the access token issued by POST /api/login (and rotated by
// POST /api/auth/refresh) to every request so those routes actually receive
// it.
api.interceptors.request.use((config) => {
  const token = authSession.getAccessToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Auth endpoints are deliberately never retried-after-refresh: refreshing in
// response to /login failing would make no sense (there's no session yet to
// refresh), refreshing in response to /auth/refresh itself failing would
// recurse, and /logout should never trigger a refresh-and-retry cycle
// either way. A 401 from /auth/refresh or /refresh-token is terminal.
const AUTH_ENDPOINT_PATTERN = /\/(login|logout|auth\/refresh|refresh-token)(\?|$)/;

const runRefresh = createSingleFlight(async () => {
  const response = await refreshClient.post('/auth/refresh');
  const { accessToken, sessionExpiresAt, user } = response.data;

  authSession.saveSession({ user, accessToken, sessionExpiresAt });
  authSession.scheduleAbsoluteLogout(sessionExpiresAt, authSession.forceLogout);
  authSession.emitRefreshed(user);

  return response.data;
});

// Response interceptor: a single 401 caused by an expired access token
// triggers exactly one /auth/refresh call (deduped across any number of
// requests that 401 at the same time via runRefresh's single-flight lock),
// then every one of those requests is retried exactly once with the new
// token. If refresh itself fails, every pending/queued request rejects and
// the app force-logs-out exactly once — never a per-request popup.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;

    if (!response || !config) {
      return Promise.reject(error);
    }

    const url = config.url || '';
    const isAuthEndpoint = AUTH_ENDPOINT_PATTERN.test(url);

    if (response.status !== 401 || isAuthEndpoint || config._retry) {
      // A 401 from /auth/refresh (or /refresh-token) itself means the
      // session cannot be restored — that's the terminal case, distinct
      // from a 401 on /login (wrong credentials, handled inline by the
      // login form, never a forced logout).
      if (response.status === 401 && isAuthEndpoint && !/\/(login|logout)(\?|$)/.test(url)) {
        authSession.forceLogout();
      }

      return Promise.reject(error);
    }

    config._retry = true;

    try {
      await runRefresh();
      return api(config);
    } catch (refreshError) {
      authSession.forceLogout();
      return Promise.reject(refreshError);
    }
  }
);

const getData = (response) => {
  return response.data?.data ?? response.data;
};

const handleError = (error, fallbackMessage = 'Something went wrong') => {
  console.error(fallbackMessage, error);

  const message =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallbackMessage;

  throw new Error(message);
};

// ================= LIBRARIES =================
export const getLibraries = async () => {
  try {
    const response = await api.get('/libraries');
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch libraries');
  }
};

export const getActiveLibraries = async () => {
  try {
    const response = await api.get('/libraries/active');
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch active libraries');
  }
};

// ================= BOOK CATEGORIES =================
export const getBookCategories = async () => {
  try {
    const response = await api.get('/book-categories');
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch book categories');
  }
};

export const getBookCategoriesByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/book-categories/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch book categories by library');
  }
};

export const getActiveBookCategoriesByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/book-categories/library/${libraryId}/active`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch active book categories');
  }
};

export const getBookCategoryById = async (id) => {
  try {
    const response = await api.get(`/book-categories/${id}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch book category');
  }
};

export const addBookCategory = async (data) => {
  try {
    const response = await api.post('/book-categories', data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to add book category');
  }
};

export const updateBookCategory = async (id, data) => {
  try {
    const response = await api.put(`/book-categories/${id}`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update book category');
  }
};

export const updateBookCategoryStatus = async (id, status) => {
  try {
    const response = await api.patch(`/book-categories/${id}/status`, { status });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update book category status');
  }
};

export const deleteBookCategory = async (id) => {
  try {
    const response = await api.delete(`/book-categories/${id}`);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to delete book category');
  }
};

// ================= BOOKS =================
export const getBooks = async () => {
  try {
    const response = await api.get('/books');
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch books');
  }
};

export const getBooksByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/books/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch books by library');
  }
};

export const getActiveBooksByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/books/library/${libraryId}/active`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch active books');
  }
};

export const searchBooksByLibrary = async (libraryId, search) => {
  try {
    const response = await api.get(`/books/library/${libraryId}/search`, {
      params: { search }
    });
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to search books');
  }
};

export const getBookById = async (id) => {
  try {
    const response = await api.get(`/books/${id}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch book');
  }
};

export const addBook = async (data) => {
  try {
    const response = await api.post('/books', data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to add book');
  }
};

export const updateBook = async (id, data) => {
  try {
    const response = await api.put(`/books/${id}`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update book');
  }
};

export const updateBookStatus = async (id, status) => {
  try {
    const response = await api.patch(`/books/${id}/status`, { status });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update book status');
  }
};

export const deleteBook = async (id) => {
  try {
    const response = await api.delete(`/books/${id}`);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to delete book');
  }
};

// ================= BOOK COPIES =================
export const getBookCopies = async () => {
  try {
    const response = await api.get('/book-copies');
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch book copies');
  }
};

export const getBookCopiesByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/book-copies/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch library book copies');
  }
};

export const getBookCopiesByBookId = async (bookId) => {
  try {
    const response = await api.get(`/book-copies/book/${bookId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch book copies by book');
  }
};

export const getAvailableCopiesByBookId = async (bookId) => {
  try {
    const response = await api.get(`/book-copies/book/${bookId}/available`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch available copies');
  }
};



export const getBookCopyById = async (id) => {
  try {
    const response = await api.get(`/book-copies/${id}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch book copy');
  }
};

export const addBookCopy = async (data) => {
  try {
    const response = await api.post('/book-copies', data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to add book copy');
  }
};

export const updateBookCopy = async (id, data) => {
  try {
    const response = await api.put(`/book-copies/${id}`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update book copy');
  }
};

export const updateBookCopyAvailability = async (id, availability_status) => {
  try {
    const response = await api.patch(`/book-copies/${id}/availability`, {
      availability_status
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update copy availability');
  }
};

export const deleteBookCopy = async (id) => {
  try {
    const response = await api.delete(`/book-copies/${id}`);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to delete book copy');
  }
};

// ================= RESERVATIONS =================
export const getReservationsByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/reservations/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch reservations');
  }
};

export const getReservationsByStatus = async (libraryId, status) => {
  try {
    const response = await api.get(`/reservations/library/${libraryId}/status/${status}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch reservations by status');
  }
};

export const approveReservationCopy = async (reservationId, data) => {
  try {
    const response = await api.patch(`/reservations/${reservationId}/approve-copy`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to approve reservation');
  }
};

export const rejectReservation = async (reservationId, data = {}) => {
  try {
    const response = await api.patch(`/reservations/${reservationId}/reject`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to reject reservation');
  }
};

export const addReservation = async (payload) => {
  const response = await api.post('/reservations', payload);
  return response.data;
};

export const cancelReservation = async (id, payload = {}) => {
  const response = await api.patch(`/reservations/${id}/cancel`, payload);
  return response.data;
};

// ================= USER DATA =================
export const getUserDataById = async (id) => {
  const response = await api.get(`/user-data/${id}`);
  return response.data?.data ?? response.data;
};

// ================= SELF-SCOPED (Parent portal "me" endpoints) =================
// These never take an id — the backend resolves "which member is this" from
// the caller's own verified login, so a Parent can only ever see their own
// profile/fines/payments, never anyone else's by changing a parameter.
export const getMyMemberProfile = async () => {
  const response = await api.get('/members/me');
  return response.data?.data ?? null;
};

export const getMyFines = async () => {
  const response = await api.get('/fines/me');
  return response.data?.data ?? [];
};

export const getMyPayments = async () => {
  const response = await api.get('/payments/me');
  return response.data?.data ?? [];
};

export const getCopyByScanCode = async (libraryId, code) => {
  const res = await api.get(
    `/book-copies/scan/${libraryId}/${encodeURIComponent(code)}`
  );

  return res.data?.data || res.data;
};

export const getMemberByScanCode = async (libraryId, code) => {
  const res = await api.get(
    `/members/scan/${libraryId}/${encodeURIComponent(code)}`
  );

  return res.data?.data || res.data;
};



// ================= ISSUES =================
export const getIssuesByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/issues/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch issues');
  }
};

export const getIssueById = async (id) => {
  try {
    const response = await api.get(`/issues/${id}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch issue');
  }
};

export const addIssue = async (data) => {
  try {
    const response = await api.post('/issues', data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to issue book');
  }
};

export const returnBookIssue = async (id, data) => {
  try {
    const response = await api.patch(`/issues/${id}/return`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to return book');
  }
};

// ================= FINES =================
export const getFinesByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/fines/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch fines');
  }
};

export const createLateFineFromIssue = async (issueId, data) => {
  try {
    const response = await api.post(`/fines/issue/${issueId}/late-fine`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to create late fine');
  }
};

export const updateFine = async (id, data) => {
  try {
    const response = await api.put(`/fines/${id}`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update fine');
  }
};

// Only unpaid/partial fines for a specific member within a specific library
// — fetched on demand once a member is picked in Create Payment, not
// prefetched for every member up front.
export const getPendingFinesByMemberAndLibrary = async (libraryId, memberId) => {
  try {
    const response = await api.get(`/fines/library/${libraryId}/member/${memberId}/pending`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch pending fines');
  }
};

// ================= MEMBER CHARGES ("Other" payments) =================
export const getPendingChargesByMemberAndLibrary = async (libraryId, memberId) => {
  try {
    const response = await api.get(`/member-charges/library/${libraryId}/member/${memberId}/pending`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch pending charges');
  }
};

export const addMemberCharge = async (data) => {
  try {
    const response = await api.post('/member-charges', data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to create charge');
  }
};

// ================= FINE RULES =================
export const getFineRulesByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/fine-rules/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch fine rules');
  }
};

export const addFineRule = async (data) => {
  try {
    const response = await api.post('/fine-rules', data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to add fine rule');
  }
};

export const updateFineRule = async (id, data) => {
  try {
    const response = await api.put(`/fine-rules/${id}`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update fine rule');
  }
};

// ================= PAYMENTS =================
export const getPaymentsByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/payments/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch payments');
  }
};

export const addPayment = async (data) => {
  try {
    const response = await api.post('/payments', data);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to add payment');
  }
};

// Full single-payment record (includes internal_order_id, gateway_order_id,
// gateway_status, verified_at, business_applied_at, library_name — none of
// which getPaymentsByLibrary above returns) — used to populate the Members
// page receipt modal.
export const getPaymentById = async (paymentId) => {
  try {
    const response = await api.get(`/payments/${paymentId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch payment receipt');
  }
};

// ================= HDFC PAYMENT GATEWAY =================
// NOTE: The backend owns all gateway secrets, order creation, signature
// verification and webhooks. The frontend only asks the backend to start a
// payment and later confirms the final status. Never trust the browser for
// amount or success — those are decided server-side.

// Ask the backend to create an HDFC order for a fine. The backend computes the
// amount from the fine record and returns whatever the checkout needs:
//   { order_id, gateway, sdk_url?, payment_session?, redirect_url? }
export const initiateFinePayment = async (fineId) => {
  try {
    const response = await api.post('/payments/gateway/order', { fine_id: fineId });
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to start payment');
  }
};

// Ask the backend to create an HDFC order for an approved "other" member
// charge. The backend loads the amount from the charge record itself — the
// frontend never sends an amount for this.
export const initiateChargePayment = async (chargeId) => {
  try {
    const response = await api.post('/payments/gateway/order', { charge_id: chargeId });
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to start payment');
  }
};

// After the gateway redirects the user back, confirm the real status from the
// backend (which re-queries HDFC). Returns { status: 'success'|'pending'|'failed', ... }.
export const getGatewayPaymentStatus = async (orderId) => {
  try {
    const response = await api.get(`/payments/gateway/status/${orderId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch payment status');
  }
};

// Re-checks (and, once HDFC confirms, applies) the status of a gateway
// refund that was previously initiated via refundPayment() below.
export const getGatewayRefundStatus = async (refundId) => {
  try {
    const response = await api.get(`/payments/gateway/refund/${refundId}/status`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch refund status');
  }
};

// Staff-only. Request a refund on an online (HDFC) payment. The refund is async
// at HDFC — this returns { refund_ref, status: 'pending' } and the final outcome
// arrives via the backend webhook. Amount is validated server-side.
export const refundPayment = async (paymentId, amount, reason) => {
  try {
    const response = await api.post('/payments/gateway/refund', {
      payment_id: paymentId,
      amount,
      reason
    });
    return getData(response);
  } catch (error) {
    handleError(error, 'Refund failed');
  }
};

export const getDepositLedgerByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/deposit-ledger/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch deposit ledger');
  }
};

// ================= MEMBERSHIP DEPOSIT PAYMENT (registration flow) =================
// Kept separate from initiateFinePayment/refundPayment above so that
// pre-existing (currently unwired) fine-payment contract is left untouched.

// Admin/Librarian only. The backend loads/validates the payable amount from
// the membership's fee configuration — never send an amount here.
export const initiateMembershipPayment = async (membershipId) => {
  try {
    const response = await api.post('/payments/gateway/order', { membership_id: membershipId });
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to start payment');
  }
};

// Backs the Create Payment modal's Membership Deposit branch — returns the
// member's latest membership in this library (or null) plus the
// server-calculated required/paid/due amounts. Never computed client-side.
export const getMembershipDepositStatus = async (libraryId, memberId) => {
  try {
    const response = await api.get(`/memberships/member/${memberId}/library/${libraryId}/deposit-status`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch membership deposit status');
  }
};

export const getMembershipsByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/memberships/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch memberships');
  }
};

export const getMembershipById = async (id) => {
  try {
    const response = await api.get(`/memberships/${id}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch membership');
  }
};

export const getMembershipFeeConfigsByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/membership-fee-configs/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch membership fee configuration');
  }
};

export const registerMember = async (payload) => {
  try {
    // FormData when a profile photo was picked (see useRegisterMemberWorkflow),
    // plain JSON otherwise — mirrors addAnnouncement's pattern.
    const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined;

    const response = await api.post('/members/register', payload, config);
    return response.data;
  } catch (error) {
    handleError(error, 'Membership registration could not be completed');
  }
};

export const getPaymentsByMembership = async (membershipId) => {
  try {
    const response = await api.get(`/payments/membership/${membershipId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch membership payments');
  }
};

// ================= MEMBERS =================
// Single fetch, cached module-wide — every screen that needs the list of
// member categories (report filters, registration form, bulk-import
// validation) calls this same function/cache instead of hardcoding its own
// value list. Values come from the live DB enum (memberModel.getMemberCategoryOptions).
let memberCategoryOptionsCache = null;
export const getMemberCategoryOptions = async () => {
  if (memberCategoryOptionsCache) return memberCategoryOptionsCache;
  try {
    const response = await api.get('/members/categories');
    memberCategoryOptionsCache = getData(response) || [];
    return memberCategoryOptionsCache;
  } catch (error) {
    handleError(error, 'Failed to fetch member categories');
  }
};

export const getMembersByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/members/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch members');
  }
};

export const searchMembersByLibrary = async (libraryId, search) => {
  try {
    const response = await api.get(`/members/library/${libraryId}/search`, {
      params: { search }
    });
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to search members');
  }
};

// ================= STUDENTS =================
export const getStudentsByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/students/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch students');
  }
};

// ================= ANNOUNCEMENTS =================
export const getAnnouncementsByLibrary = async (libraryId) => {
  try {
    const response = await api.get(`/announcements/library/${libraryId}`);
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch announcements');
  }
};

// Fetch a single announcement's full detail (includes attachments, which the
// list endpoint may omit). Returns null on failure so callers can fall back.
export const getAnnouncementById = async (id) => {
  try {
    const response = await api.get(`/announcements/${id}`);
    return getData(response);
  } catch (error) {
    handleError('Failed to fetch announcement detail', error);
    return null;
  }
};

export const addAnnouncement = async (data) => {
  try {
    // When `data` is FormData (announcement + attachments), send as multipart.
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    const config = isFormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : undefined;

    const response = await api.post('/announcements', data, config);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to add announcement');
  }
};

// export const updateAnnouncement = async (id, data) => {
//   try {
//     const response = await api.put(`/announcements/${id}`, data);
//     return response.data;
//   } catch (error) {
//     handleError(error, 'Failed to update announcement');
//   }
//


export const updateAnnouncement = async (id, payload) => {
  try {
    // console.log('UPDATE ANNOUNCEMENT ID:', id);
    // console.log('UPDATE ANNOUNCEMENT PAYLOAD:', payload);

    const config = {};

    if (payload instanceof FormData) {
      config.headers = {
        'Content-Type': 'multipart/form-data'
      };
    }

    const response = await api.put(`/announcements/${id}`, payload, config);
    return response.data;
  } catch (error) {
    console.error('UPDATE ANNOUNCEMENT ERROR:', error.response?.data || error);

    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.response?.data?.sqlMessage ||
      error.response?.data?.details ||
      'Failed to update announcement';

    throw new Error(message);
  }
};


export const deleteAnnouncement = async (id) => {
  try {
    const response = await api.delete(`/announcements/${id}`);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to delete announcement');
  }
};

export const getErrorMessage = (error) => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    'Something went wrong'
  );
};
// ================= MANUAL FINE CREATE =================
export const addFine = async (payload) => {
  const response = await api.post('/fines', payload);
  return response.data;
};



export const addRefund = async (payload) => {
  const response = await api.post('/refunds', payload);
  return response.data;
};

// ================= ISSUE RENEWALS =================
export const getIssueRenewalsByLibrary = async (libraryId) => {
  const response = await api.get(`/issue-renewals/library/${libraryId}`);
  return getData(response);
};

// Renewal history for a single issue — used by the Issued Book List's
// read-only "Issue Details" modal.
export const getIssueRenewalsByIssueId = async (issueId) => {
  const response = await api.get(`/issue-renewals/issue/${issueId}`);
  return getData(response);
};

export const addIssueRenewal = async (payload) => {
  const response = await api.post('/issue-renewals', payload);
  return response.data;
};

// ================= NOTIFICATIONS =================
export const getNotificationsByLibrary = async (libraryId) => {
  const response = await api.get(`/notifications/library/${libraryId}`);
  return getData(response);
};

export const markNotificationAsRead = async (id) => {
  try {
    const response = await api.patch(`/notifications/${id}/read`);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to mark notification as read');
  }
};

// ================= STUDENT CREATE / IMPORT =================
export const addStudent = async (payload) => {
  const response = await api.post('/students', payload);
  return response.data;
};

export const updateStudent = async (id, payload) => {
  try {
    const response = await api.put(`/students/${id}`, payload);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update student');
  }
};

export const getMemberFines = async (memberId) => {
  const res = await api.get(`/fines/member/${memberId}`);
  return res.data?.data || [];
};


// ================= REFUNDS =================
export const getRefundsByLibrary = async (libraryId) => {
  const response = await api.get(`/refunds/library/${libraryId}`);
  return getData(response);
};

// ================= REPORTS =================
// Every function here returns the FULL response envelope (not getData()'s
// unwrapped `.data`) — reports carry `meta` (pagination) and sometimes
// `totals` as siblings of `data`, both needed by the report page shell.
export const getMemberIssueHistoryReport = async (libraryId, memberId, params = {}) => {
  try {
    const response = await api.get(`/reports/library/${libraryId}/member-issue-history/${memberId}`, { params });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch member issue history');
  }
};

export const getBookIssueHistoryReport = async (libraryId, bookId, params = {}) => {
  try {
    const response = await api.get(`/reports/library/${libraryId}/book-issue-history/${bookId}`, { params });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch book issue history');
  }
};

export const getIssuesReport = async (libraryId, params = {}) => {
  try {
    const response = await api.get(`/reports/library/${libraryId}/issues`, { params });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch issues report');
  }
};

export const getIssuesByMemberCategoryReport = async (libraryId, params = {}) => {
  try {
    const response = await api.get(`/reports/library/${libraryId}/issues-by-member-category`, { params });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch issues by member category');
  }
};

export const getOverdueBooksReport = async (libraryId, params = {}) => {
  try {
    const response = await api.get(`/reports/library/${libraryId}/overdue-books`, { params });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch overdue books report');
  }
};

export const getFineCollectionReport = async (libraryId, params = {}) => {
  try {
    const response = await api.get(`/reports/library/${libraryId}/fine-collection`, { params });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch fine collection report');
  }
};

export const getPendingFineReport = async (libraryId, params = {}) => {
  try {
    const response = await api.get(`/reports/library/${libraryId}/pending-fines`, { params });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch pending fine report');
  }
};

export const getDailyIssueReturnReport = async (libraryId, params = {}) => {
  try {
    const response = await api.get(`/reports/library/${libraryId}/daily-issue-return`, { params });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch daily issue & return report');
  }
};

export const getMostIssuedBooksReport = async (libraryId, params = {}) => {
  try {
    const response = await api.get(`/reports/library/${libraryId}/most-issued-books`, { params });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch most issued books report');
  }
};

export const getMostActiveMembersReport = async (libraryId, params = {}) => {
  try {
    const response = await api.get(`/reports/library/${libraryId}/most-active-members`, { params });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch most active members report');
  }
};

export const getLostDamagedReport = async (libraryId, params = {}) => {
  try {
    const response = await api.get(`/reports/library/${libraryId}/lost-damaged`, { params });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch lost/damaged books report');
  }
};

export const getReservationReportData = async (libraryId, params = {}) => {
  try {
    const response = await api.get(`/reports/library/${libraryId}/reservations`, { params });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch reservation/hold report');
  }
};

// ================= AUTH / OTP =================
export const sendMobileOtp = async (mobile) => {
  try {
    const response = await api.post('/send-otp', { mobile });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to send OTP');
  }
};

export const verifyMobileOtp = async (mobile, otp) => {
  try {
    const response = await api.post('/verify-otp', { mobile, otp });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to verify OTP');
  }
};

export const loginUser = async (payload) => {
  try {
    const response = await api.post('/login', payload, { timeout: 15000 });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to login');
  }
};

// Best-effort server-side revocation of the refresh-token cookie. Called by
// Auth.js's logout() before it clears local state — a failure here (e.g. the
// network is already down) must never block the local logout that follows.
export const logoutUser = async () => {
  const response = await api.post('/logout');
  return response.data;
};

export const registerUser = async (formData) => {
  try {
    const response = await api.post('/register', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  } catch (error) {
    handleError(error, 'Failed to register user');
  }
};

export const sendPasswordResetOtp = async (email) => {
  try {
    const response = await api.post('/send-password-reset-otp', { email });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to send password reset OTP');
  }
};

export const verifyEmailOtp = async (email, emailOtp) => {
  try {
    const response = await api.post('/verify-email-otp', {
      email,
      email_otp: emailOtp
    });

    return response.data;
  } catch (error) {
    handleError(error, 'Failed to verify email OTP');
  }
};

export const resetPassword = async (email, newPassword) => {
  try {
    const response = await api.post('/reset-password', {
      email,
      sent_password: newPassword
    });

    return response.data;
  } catch (error) {
    handleError(error, 'Failed to reset password');
  }
};

export const getRolesList = async () => {
  try {
    const response = await api.get('/roles_list');
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch roles');
  }
};

export const getRegisterLibraries = async () => {
  try {
    const response = await api.get('/libraries/');
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch libraries');
  }
};

export const addMember = async (payload) => {
  try {
    const response = await api.post('/members', payload);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to add member');
  }
};

// ================= ADMIN: SCHOOLS / LIBRARIES =================
// NOTE: These hit the same backend base as everything else. Library records are
// the "schools" in this system. Endpoints were confirmed against the legacy
// admin pages (Library_List, Add_User). If the backend later renames them,
// only these wrappers need to change.

// List every library/school (admin scope — not filtered by the caller's library).
export const getAllLibrariesAdmin = async () => {
  try {
    const response = await api.get('/add_new_library_data');
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch libraries');
  }
};

// Create a library/school. Body: { library_name, library_code, employee_authorization_list_id }
export const addLibraryAdmin = async (payload) => {
  try {
    const response = await api.post('/add_library_book_type', payload);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to add library');
  }
};

// Update a library/school by id. Same body as add.
export const updateLibraryAdmin = async (id, payload) => {
  try {
    const response = await api.put(`/update_Library_book_type/${id}`, payload);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update library');
  }
};

export const deleteLibraryAdmin = async (id) => {
  try {
    const response = await api.delete(`/delete_library/${id}`);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to delete library');
  }
};

// ================= ADMIN: USERS / LIBRARIANS =================
// Full list of authorised employees (admins, librarians, staff) across libraries.
export const getEmployeeListAdmin = async () => {
  try {
    const response = await api.get('/auth_employee_list');
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch users');
  }
};

// Slim list used for the "employee authorization" dropdown when creating a library.
export const getLibrarianAuthList = async () => {
  try {
    const response = await api.get('/auth_librarian_list');
    return getData(response);
  } catch (error) {
    handleError(error, 'Failed to fetch librarians');
  }
};

// Activate / deactivate a user. Backend expects POST with { status: 0 | 1 }.
export const updateUserStatusAdmin = async (id, status) => {
  try {
    const response = await api.post(`/update_user_status/${id}`, { status });
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update user status');
  }
};

export const updateMember = async (id, payload) => {
  try {
    // FormData when a new profile photo was picked, plain JSON otherwise —
    // mirrors registerMember/addAnnouncement's pattern.
    const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined;

    const response = await api.put(`/members/${id}`, payload, config);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update member');
  }
};


export default api;